import React from "react";
import styled from "styled-components";
import { BarcodeReaderStatus } from "../../types/HoneywellBarcodeReaderStatusTypes";
import { SymbologyType } from "../../types/SymbologyType";

interface LicenseInfo {
  licenseStatus: string;
  expirationDate: string;
  daysToExpiration: number;
  feature: string;
  version: string;
}

interface LicenseResponse {
  status: number;
  licenseStatus: string;
  expirationDate: string;
  daysToExpiration: number;
  feature: string;
  version: string;
}

interface BarcodeReaderResult {
  status: BarcodeReaderStatus;
  message: string;
}

interface BarcodeReader {
  getLicenseInfo(callback: (result: LicenseResponse) => void): void;
  addEventListener(
    event: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (...args: any[]) => void,
    useCapture: boolean
  ): void;
}
declare const BarcodeReader: {
  new (
    deviceId: string | null,
    callback: (result: unknown) => void
  ): BarcodeReader;
};

const InfoContainer = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const InfoHeader = styled.h3`
  margin: 0 0 10px 0;
`;

const InfoParagraph = styled.p`
  margin: 5px 0;
  word-wrap: break-word;
  max-width: 100%;
`;

const InfoValue = styled.span`
  max-width: 100%;
  display: inline-block;
  vertical-align: top;
  word-wrap: break-word;
`;

interface InfoBoxProps {
  title: string;
  value: string;
}

const InfoBox: React.FC<InfoBoxProps> = ({ title, value }) => {
  return (
    <InfoParagraph>
      <strong>{title}: </strong> <InfoValue>{value}</InfoValue>
    </InfoParagraph>
  );
};

const HoneywellScannerDebugPage: React.FC = () => {
  const [barcodeReaderStatusCode, setBarcodeReaderStatusCode] =
    React.useState<BarcodeReaderStatus | null>(null);
  const [barcodeReaderStatusMessage, setBarcodeReaderStatusMessage] =
    React.useState("loading");
  const [licenseInfo, setLicenseInfo] = React.useState<LicenseInfo | null>(
    null
  );

  const [scannedValue, setScannedValue] = React.useState<string | null>(null);
  const [scannedSymbologyType, setScannedSymbologyType] =
    React.useState<SymbologyType | null>(null);

  React.useEffect(() => {
    function onLicenseInfoComplete(lic: LicenseResponse) {
      if (lic.status === 0) {
        try {
          const parsedLicense = {
            licenseStatus: lic.licenseStatus || "Unknown",
            expirationDate: new Date(lic.expirationDate).toLocaleDateString(),
            daysToExpiration: lic.daysToExpiration || 0,
            feature: lic.feature || "Unknown",
            version: lic.version || "Unknown",
          };
          setLicenseInfo(parsedLicense);
        } catch {
          setLicenseInfo(null);
        }
      }
    }

    const defaultScanner = new BarcodeReader(null, (result: unknown) => {
      const barcodeResult = result as BarcodeReaderResult;
      onBarcodeReaderComplete(barcodeResult);
    });

    const barcodeRead = (data: string, type: SymbologyType, time: string) => {
      console.log("Barcode data received:", data, type, time);
      setScannedValue(data);
      setScannedSymbologyType(type);
    };

    function onBarcodeReaderComplete(result: BarcodeReaderResult) {
      if (result.status === 0) {
        defaultScanner.getLicenseInfo(onLicenseInfoComplete);
        defaultScanner.addEventListener("barcodedataready", barcodeRead, false);
        console.log("BarcodeReader created successfully:", result);
        setBarcodeReaderStatusCode(result.status);
        setBarcodeReaderStatusMessage(result.message);
      } else {
        alert("Failed to create BarcodeReader, " + result.message);
        setBarcodeReaderStatusCode(result.status);
        setBarcodeReaderStatusMessage(result.message || "Unknown error");
      }
    }
  }, []);
  return (
    <div style={{ padding: "20px" }}>
      <InfoContainer>
        <InfoHeader>Barcode Reader Status</InfoHeader>
        <InfoBox
          title="Status"
          value={barcodeReaderStatusCode?.toString() ?? ""}
        />
        <InfoBox title="Message" value={barcodeReaderStatusMessage} />
      </InfoContainer>
      <InfoContainer>
        <InfoHeader>Scanned Information</InfoHeader>
        <div>
          <InfoBox title="Scanned Value" value={scannedValue ?? ""} />
          <InfoBox
            title="Scanned Symbology Type"
            value={scannedSymbologyType?.toString() ?? ""}
          />
        </div>
      </InfoContainer>
      <InfoContainer>
        <InfoHeader>License Information</InfoHeader>
        {licenseInfo ? (
          <div>
            <InfoBox title="Status" value={licenseInfo.licenseStatus} />
            <InfoBox
              title="Expiration Date"
              value={licenseInfo.expirationDate}
            />
            <InfoBox
              title="Days until expiration"
              value={licenseInfo.daysToExpiration.toString()}
            />
            <InfoBox title="Feature" value={licenseInfo.feature} />
            <InfoBox title="Version" value={licenseInfo.version} />
          </div>
        ) : (
          <p>No license information available</p>
        )}
      </InfoContainer>
    </div>
  );
};

export default HoneywellScannerDebugPage;
